import type { JSONContent } from "@tiptap/react";
import type { ContentNode } from "@/types/template";
import { parseFieldDef } from "@/lib/parseFieldDef";

function textContent(node: JSONContent): string {
  return (node.content ?? []).map((n) => n.text ?? "").join("");
}

function flushParas(pending: string[], nodes: ContentNode[]): string[] {
  if (pending.length > 0) {
    nodes.push({ markdown: pending.join("\n"), type: "text" });
  }
  return [];
}

function parseFieldNode(node: JSONContent, nodes: ContentNode[]) {
  const field = parseFieldDef((node.attrs?.fieldDef as string | undefined) ?? "");
  if (field) {
    nodes.push({ field, type: "field" });
  }
}

function processNode(node: JSONContent, nodes: ContentNode[], pending: string[]): string[] {
  switch (node.type) {
    case "section": {
      pending = flushParas(pending, nodes);
      nodes.push({
        id: (node.attrs?.sectionId as string | undefined) ?? "",
        required: (node.attrs?.required as boolean | undefined) ?? false,
        title: textContent(node),
        type: "section",
      });
      return pending;
    }
    case "heading": {
      pending = flushParas(pending, nodes);
      nodes.push({
        level: (node.attrs?.level as number | undefined) ?? 2,
        text: textContent(node),
        type: "heading",
      });
      return pending;
    }
    case "paragraph": {
      pending.push(textContent(node));
      return pending;
    }
    case "fieldNode": {
      pending = flushParas(pending, nodes);
      parseFieldNode(node, nodes);
      return pending;
    }
    case "scoregroupNode": {
      pending = flushParas(pending, nodes);
      nodes.push({ id: (node.attrs?.id as string | undefined) ?? "", type: "scoregroup" });
      return pending;
    }
    default: {
      return pending;
    }
  }
}

/** Convert a TipTap JSONContent document back to ContentNode[]. Reverse of contentToNodes(). */
export function documentToContentNodes(doc: JSONContent): ContentNode[] {
  const nodes: ContentNode[] = [];
  let pendingParaLines: string[] = [];

  for (const node of doc.content ?? []) {
    pendingParaLines = processNode(node, nodes, pendingParaLines);
  }

  if (pendingParaLines.length > 0) {
    nodes.push({ markdown: pendingParaLines.join("\n"), type: "text" });
  }
  return nodes;
}
